import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Route } from "lucide-react";
import { JOURNEY_ITEMS, EDITABLE_FIELDS } from "@/lib/journeyCatalog";

const PHASE_LABELS = {
  "0": "Phase 0 · The Free Call",
  "1": "Phase 1 · The Blueprint",
  "2": "Phase 2 · Build your move",
  "3": "Phase 3 · The Wingman",
};

export function JourneyPanel({ axios }) {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`/admin/journey-content`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((o) => (map[o.slug] = o));
        setOverrides(map);
      })
      .catch(() => toast.error("Could not load Journey content."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phases = ["0", "1", "2", "3"];

  return (
    <div data-testid="journey-panel">
      <div className="mb-8 max-w-3xl">
        <h2 className="font-serif text-3xl tracking-tight flex items-center gap-3">
          <Route size={22} /> Journey
        </h2>
        <p className="text-[#6B4954] text-sm mt-1">
          Edit the text and prices shown in the public "Journey" section. The
          phases, order and images are fixed — only the wording and prices below
          are editable. Leave a field blank to keep the original default.
        </p>
      </div>

      {loading ? (
        <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
      ) : (
        <div className="space-y-10">
          {phases.map((ph) => {
            const items = JOURNEY_ITEMS.filter((i) => i.phase === ph);
            return (
              <div key={ph}>
                <h3 className="text-[11px] tracking-[0.24em] uppercase text-[#451C25] mb-4 pb-2 border-b border-[#451C25]/15">
                  {PHASE_LABELS[ph]}
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {items.map((item) => (
                    <JourneyItemEditor
                      key={item.slug}
                      item={item}
                      override={overrides[item.slug]}
                      axios={axios}
                      onSaved={fetchAll}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function effectiveValue(item, override, field) {
  const ov = override?.[field];
  if (ov !== undefined && ov !== null && ov !== "") return ov;
  return item[field] || "";
}

function JourneyItemEditor({ item, override, axios, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: effectiveValue(item, override, "title"),
    price: effectiveValue(item, override, "price"),
    price_note: effectiveValue(item, override, "price_note"),
    description: effectiveValue(item, override, "description"),
  }));
  const [saving, setSaving] = useState(false);
  const isOverridden = !!override;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/admin/journey-content/${item.slug}`, form);
      toast.success(`${item.title} saved.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${item.title}" back to the original text?`))
      return;
    setSaving(true);
    try {
      await axios.delete(`/admin/journey-content/${item.slug}`);
      setForm({
        title: item.title || "",
        price: item.price || "",
        price_note: item.price_note || "",
        description: item.description || "",
      });
      toast.success(`${item.title} reset to default.`);
      onSaved?.();
    } catch (e) {
      toast.error("Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  const kindLabel = {
    solo: "Step",
    scout: "Featured",
    pack: "Pack",
    premium: "Premium",
  }[item.section];

  return (
    <div
      data-testid={`journey-item-${item.slug}`}
      className="bg-white border border-[#451C25]/12 p-5 md:p-6"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="text-[10px] tracking-[0.2em] uppercase bg-[#EDE4D6] text-[#6B4954] px-2 py-0.5">
            {kindLabel}
          </span>
          <span className="text-[11px] text-[#6B4954] font-mono">
            {item.slug}
          </span>
          {isOverridden && (
            <span className="text-[10px] tracking-[0.18em] uppercase bg-[#451C25] text-white px-2 py-0.5">
              Edited
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 mb-4">
        <MiniField
          label="Title"
          value={form.title}
          on={(v) => upd("title", v)}
          testid={`journey-title-${item.slug}`}
        />
        <MiniField
          label="Price"
          value={form.price}
          on={(v) => upd("price", v)}
          testid={`journey-price-${item.slug}`}
        />
        <MiniField
          label="Price note"
          value={form.price_note}
          on={(v) => upd("price_note", v)}
          placeholder="one-time / per year..."
          testid={`journey-pricenote-${item.slug}`}
        />
      </div>

      <div className="mb-5">
        <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
          Description
        </label>
        <textarea
          data-testid={`journey-desc-${item.slug}`}
          value={form.description}
          onChange={(e) => upd("description", e.target.value)}
          rows={3}
          className="w-full bg-transparent border border-[#451C25]/20 p-3 text-[14px] leading-relaxed focus:outline-none focus:border-[#451C25] transition-colors"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid={`journey-save-${item.slug}`}
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#451C25] disabled:opacity-50 text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
        >
          <Save size={13} /> {saving ? "Saving..." : "Save"}
        </button>
        {isOverridden && (
          <button
            data-testid={`journey-reset-${item.slug}`}
            onClick={reset}
            disabled={saving}
            className="inline-flex items-center gap-2 border border-[#451C25]/25 text-[#451C25] px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"
          >
            <RotateCcw size={13} /> Reset to default
          </button>
        )}
      </div>
    </div>
  );
}

function MiniField({ label, value, on, testid, placeholder }) {
  return (
    <div className="md:min-w-[150px]">
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#451C25]/20 py-2 text-[14px] focus:outline-none focus:border-[#451C25] transition-colors"
      />
    </div>
  );
}
